import { useCallback, useEffect, useRef, useState } from "react";
import { message as antMessage } from "antd";
import agentService, {
  ChatStreamEvent,
  ChatHistoryMessage,
} from "../../../../../api/agent/agentService";
import {
  AskAiMessage,
  Attachment,
  AttachmentMeta,
  ToolEventEntry,
} from "./types";

interface UseAskAiSessionOptions {
  tenderRef: string | number | undefined;
}

interface SendOptions {
  text: string;
  attachments: Attachment[];
  onUploadFinished?: (filename: string) => void;
}

export interface RestoredSessionMeta {
  sessionId: string;
  title?: string;
  messageCount: number;
  lastMessageAt?: number;
}

interface UseAskAiSessionResult {
  messages: AskAiMessage[];
  setMessages: React.Dispatch<React.SetStateAction<AskAiMessage[]>>;
  sessionId: string | null;
  sessionLoading: boolean;
  isStreaming: boolean;
  thinkingLabel: string;
  thinkingStep: number;
  // Populated once on mount if the tender has a prior conversation. Consumer
  // can use this to prompt "resume or start new?" before showing the chat.
  restoredSession: RestoredSessionMeta | null;
  send: (opts: SendOptions) => Promise<void>;
  regenerateLast: (opts: { attachments?: Attachment[] }) => Promise<void>;
  cancelStreaming: () => void;
  resetSession: () => void;
  loadSession: (sessionId: string) => Promise<number>;
}

// Static rotation shown when the agent hasn't fired a tool event yet (or
// between tool calls). Procurement-flavoured so the wait feels intentional
// rather than generic. Cycles every ~1.1s via the thinking timer.
const THINKING_STEPS = [
  "Reading your question",
  "Reviewing tender data",
  "Cross-checking supplier records",
  "Comparing bids and scores",
  "Surfacing key insights",
  "Drafting a response",
  "Almost there",
];

const restoreHistory = (
  hist: any
): AskAiMessage[] => {
  const rows: ChatHistoryMessage[] = hist?.messages || [];
  return rows.map((m) => ({
    role: m.role,
    text: m.content || "",
    ts: m.created_at ? new Date(m.created_at).getTime() : Date.now(),
    messageId: m.message_id,
    citations:
      m.cited_chunk_ids ||
      m.cited_web_urls ||
      m.cited_silver_rows ||
      m.cited_sources
        ? {
            chunks: m.cited_chunk_ids,
            urls: m.cited_web_urls,
            silver: m.cited_silver_rows,
            sources: m.cited_sources,
          }
        : undefined,
    suggestedQuestions:
      Array.isArray(m.suggested_questions) && m.suggested_questions.length > 0
        ? m.suggested_questions
        : undefined,
  }));
};

export function useAskAiSession({
  tenderRef,
}: UseAskAiSessionOptions): UseAskAiSessionResult {
  const [messages, setMessages] = useState<AskAiMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingLabel, setThinkingLabel] = useState("Thinking");
  const [thinkingStep, setThinkingStep] = useState(0);
  const [restoredSession, setRestoredSession] =
    useState<RestoredSessionMeta | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const thinkingTimerRef = useRef<number | null>(null);

  // Mount: hydrate from the most recent session for this tender.
  useEffect(() => {
    let cancelled = false;
    setRestoredSession(null);
    const load = async () => {
      if (!tenderRef) {
        setSessionLoading(false);
        return;
      }
      setSessionLoading(true);
      try {
        const list: any = await agentService.listChatSessions({
          tender_id: tenderRef,
          limit: 1,
        });
        const sessions =
          (Array.isArray(list?.sessions) && list.sessions) || [];
        if (cancelled) return;
        if (sessions.length === 0) {
          setSessionId(null);
          setMessages([]);
          return;
        }
        const latest = sessions[0];
        setSessionId(latest.session_id);
        const hist: any = await agentService.getChatHistory(latest.session_id);
        if (cancelled) return;
        const restored = restoreHistory(hist);
        setMessages(restored);
        if (restored.length > 0) {
          setRestoredSession({
            sessionId: latest.session_id,
            title: latest.title,
            messageCount: restored.length,
            lastMessageAt: restored[restored.length - 1].ts,
          });
        }
      } catch (err) {
        console.error("Failed to load chat session", err);
        if (!cancelled) setSessionId(null);
      } finally {
        if (!cancelled) setSessionLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tenderRef]);

  // Unmount cleanup: kill timers + abort any in-flight stream.
  useEffect(() => {
    return () => {
      if (thinkingTimerRef.current)
        window.clearInterval(thinkingTimerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Thinking-step rotation while streaming.
  useEffect(() => {
    if (thinkingTimerRef.current) {
      window.clearInterval(thinkingTimerRef.current);
      thinkingTimerRef.current = null;
    }
    if (isStreaming) {
      setThinkingStep(0);
      thinkingTimerRef.current = window.setInterval(() => {
        setThinkingStep((s) => (s + 1) % THINKING_STEPS.length);
      }, 1100);
    }
    return () => {
      if (thinkingTimerRef.current) {
        window.clearInterval(thinkingTimerRef.current);
        thinkingTimerRef.current = null;
      }
    };
  }, [isStreaming]);

  const cancelStreaming = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const resetSession = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setMessages([]);
    setSessionId(null);
    setIsStreaming(false);
    setThinkingLabel("Thinking");
    setRestoredSession(null);
  }, []);

  const loadSession = useCallback(
    async (sid: string): Promise<number> => {
      if (!sid) return 0;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setSessionLoading(true);
      try {
        const hist: any = await agentService.getChatHistory(sid);
        const restored = restoreHistory(hist);
        setSessionId(sid);
        setMessages(restored);
        // User explicitly picked this session — clear the resume prompt
        // so it doesn't show on top of the freshly loaded chat.
        setRestoredSession(null);
        return restored.length;
      } catch (err: any) {
        console.error("Failed to load chat session", err);
        antMessage.error(err?.message || "Could not load that conversation");
        throw err;
      } finally {
        setSessionLoading(false);
      }
    },
    []
  );

  // Internal helper that runs the SSE stream for a given user message.
  const runStream = useCallback(
    async (
      sid: string,
      userText: string,
      attachments: Attachment[],
      onUploadFinished?: (filename: string) => void
    ) => {
      // Append placeholder assistant message and capture its index.
      let assistantIdx = -1;
      setMessages((prev) => {
        assistantIdx = prev.length;
        return [
          ...prev,
          {
            role: "assistant",
            text: "",
            ts: Date.now() + 1,
            pending: true,
            toolEvents: [],
          },
        ];
      });
      setIsStreaming(true);
      setThinkingLabel("Thinking");

      const patchAssistant = (patch: Partial<AskAiMessage>) => {
        setMessages((prev) => {
          if (assistantIdx < 0 || assistantIdx >= prev.length) return prev;
          const next = prev.slice();
          next[assistantIdx] = { ...next[assistantIdx], ...patch };
          return next;
        });
      };

      const appendDelta = (delta: string) => {
        setMessages((prev) => {
          if (assistantIdx < 0 || assistantIdx >= prev.length) return prev;
          const next = prev.slice();
          const cur = next[assistantIdx];
          next[assistantIdx] = {
            ...cur,
            text: (cur.text || "") + delta,
            pending: true,
          };
          return next;
        });
      };

      const appendToolEvent = (entry: ToolEventEntry) => {
        setMessages((prev) => {
          if (assistantIdx < 0 || assistantIdx >= prev.length) return prev;
          const next = prev.slice();
          const cur = next[assistantIdx];
          next[assistantIdx] = {
            ...cur,
            toolEvents: [...(cur.toolEvents || []), entry],
          };
          return next;
        });
      };

      // Strip leading emoji/whitespace from a label like
      // "🔧 Looking up tender data…" → "Looking up tender data…".
      const cleanEmojiPrefix = (s: string): string =>
        s
          ?.replace(
            /^[\u{2700}-\u{27BF}\u{1F300}-\u{1FAFF}\u{2600}-\u{26FF}✓✔\s]+/u,
            ""
          )
          ?.trim() || "";

      const onEvent = (evt: ChatStreamEvent) => {
        switch (evt.type) {
          case "file_uploaded":
            appendToolEvent({
              kind: "upload",
              label: `Uploaded ${evt.data?.filename}`,
            });
            if (evt.data?.filename) onUploadFinished?.(evt.data.filename);
            break;
          case "user_message_persisted":
            break;
          case "tool_call": {
            const d: any = evt.data || {};
            const friendly =
              d.label ||
              cleanEmojiPrefix(d.display_text) ||
              d.name ||
              "tool";
            setThinkingLabel(friendly);
            if (d.name) {
              appendToolEvent({
                kind: "tool",
                name: d.name,
                label: friendly,
                state: "active",
              });
            }
            break;
          }
          case "tool_result": {
            const d: any = evt.data || {};
            const friendly =
              d.label ||
              cleanEmojiPrefix(d.display_text) ||
              d.name ||
              "tool";
            if (d.name) {
              appendToolEvent({
                kind: "tool",
                name: d.name,
                label: friendly,
                state: "done",
              });
            }
            setThinkingLabel("Drafting a response");
            break;
          }
          case "assistant_delta":
            appendDelta(evt.data?.text || "");
            break;
          case "assistant_done": {
            const suggestions = Array.isArray(evt.data?.suggested_questions)
              ? evt.data.suggested_questions.filter(
                  (q: any) => typeof q === "string" && q.trim().length > 0
                )
              : undefined;
            patchAssistant({
              text: evt.data?.text || "",
              messageId: evt.data?.message_id,
              citations: evt.data?.citations,
              suggestedQuestions:
                suggestions && suggestions.length > 0 ? suggestions : undefined,
              pending: false,
            });
            break;
          }
          case "error":
            patchAssistant({
              text: "Sorry, something went wrong while generating a response.",
              pending: false,
              error: true,
            });
            break;
        }
      };

      const abort = new AbortController();
      abortRef.current = abort;
      try {
        if (attachments.length > 0) {
          await agentService.sendChatMessageMultipart(
            sid,
            {
              content: userText || "(attachment only)",
              stream: true,
              tender_id: tenderRef,
              files: attachments.map((a) => a.file),
            },
            onEvent,
            abort.signal
          );
        } else {
          await agentService.sendChatMessageText(
            sid,
            { content: userText, stream: true },
            onEvent,
            abort.signal
          );
        }
        // Safety net: if assistant_done never fired, clear pending so the
        // bubble doesn't get stuck spinning forever.
        patchAssistant({ pending: false });
      } catch (err: any) {
        console.error("Chat send failed", err);
        if (err?.name === "AbortError") {
          patchAssistant({ text: "(Stopped)", pending: false });
        } else {
          patchAssistant({
            text:
              err?.message ||
              "Sorry, the AI service is unavailable right now. Please try again.",
            pending: false,
            error: true,
          });
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [tenderRef]
  );

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const created = await agentService.createChatSession({
      tender_id: tenderRef,
      title: `Conversation about ${tenderRef || "tender"}`,
    });
    const sid = created?.session_id;
    if (!sid) throw new Error("Could not create chat session");
    setSessionId(sid);
    return sid;
  }, [sessionId, tenderRef]);

  const send = useCallback(
    async ({ text, attachments, onUploadFinished }: SendOptions) => {
      const trimmed = text.trim();
      if (!trimmed && attachments.length === 0) return;
      if (isStreaming) return;
      if (sessionLoading) {
        antMessage.info("Still restoring your last conversation…");
        return;
      }

      const attachmentMeta: AttachmentMeta[] = attachments.map((a) => ({
        name: a.file.name,
        size: a.file.size,
      }));
      const userMsg: AskAiMessage = {
        role: "user",
        text: trimmed || "(attachment only)",
        ts: Date.now(),
        attachments: attachmentMeta.length > 0 ? attachmentMeta : undefined,
      };
      setMessages((prev) => [...prev, userMsg]);

      try {
        const sid = await ensureSession();
        await runStream(sid, trimmed, attachments, onUploadFinished);
      } catch (err: any) {
        console.error("send failed", err);
        antMessage.error(err?.message || "Failed to send message");
        setIsStreaming(false);
      }
    },
    [ensureSession, isStreaming, runStream, sessionLoading]
  );

  // Pop the most-recent assistant message (and replay the user prompt that
  // produced it). The "regenerate: true" intent is purely client-side; the
  // gateway sees a fresh user message.
  const regenerateLast = useCallback(
    async ({ attachments = [] }: { attachments?: Attachment[] }) => {
      if (isStreaming) return;
      let promptText = "";
      setMessages((prev) => {
        // Find the last assistant turn.
        let asstIdx = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].role === "assistant") {
            asstIdx = i;
            break;
          }
        }
        if (asstIdx <= 0) return prev;
        // The user message that triggered it should be immediately prior.
        const userMsg = prev[asstIdx - 1];
        if (!userMsg || userMsg.role !== "user") return prev;
        promptText = userMsg.text;
        // Drop the assistant message; keep the user prompt so it isn't
        // duplicated when we re-run.
        return prev.slice(0, asstIdx);
      });
      if (!promptText) return;
      if (sessionLoading) {
        antMessage.info("Still restoring your last conversation…");
        return;
      }
      try {
        const sid = await ensureSession();
        await runStream(sid, promptText, attachments);
      } catch (err: any) {
        console.error("regenerate failed", err);
        antMessage.error(err?.message || "Failed to regenerate");
        setIsStreaming(false);
      }
    },
    [ensureSession, isStreaming, runStream, sessionLoading]
  );

  return {
    messages,
    setMessages,
    sessionId,
    sessionLoading,
    isStreaming,
    thinkingLabel,
    thinkingStep,
    restoredSession,
    send,
    regenerateLast,
    cancelStreaming,
    resetSession,
    loadSession,
  };
}

export const ASK_AI_THINKING_STEPS = THINKING_STEPS;
